import { Edit, OpenInNew } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  MenuList,
  Popover,
  Select,
  Typography,
} from "@mui/material";
import { endOfMonth, endOfYear, format, parseISO, subMonths, subYears } from "date-fns";
import { useMemo, useCallback, useState } from "react";

import { updateCustomerIndvidual } from "../../../../../api/customers";
import { generateTargets } from "../../../../../api/customerCustomTargets";
import { assertSectionControllerContract } from "../../../../shared/management/sections/contracts/sectionControllerContract";
import BookkeeperMultiSelectPopover from "../../../../shared/management/sections/shared/table/BookkeeperMultiSelectPopover";
import MultiSelectSortFilterPopover from "../../../../shared/management/sections/shared/table/MultiSelectSortFilterPopover";
import { createManagementCellRenderer } from "../../../../shared/management/sections/shared/table/createManagementCellRenderer";
import useReportsTabData from "./useReportsTabData";

const DASHBOARD_DATE_PREDEFINED_OPTIONS = [
  { label: "Last Month", value: "last_month" },
  { label: "Year-to-date", value: "year_to_date" },
  { label: "Last Year", value: "last_year" },
];

function getDashboardDateMonthOptions() {
  const options = [];
  const now = new Date();

  for (let index = 0; index < 12; index += 1) {
    const monthDate = subMonths(now, index);
    options.push({
      label: format(monthDate, "MMMM yyyy"),
      value: format(monthDate, "yyyy-MM"),
    });
  }

  return options;
}

function getDashboardDateEndDate(value) {
  const now = new Date();

  if (value === "last_month") return endOfMonth(subMonths(now, 1));
  if (value === "year_to_date") return endOfMonth(now);
  if (value === "last_year") return endOfYear(subYears(now, 1));

  if (/^\d{4}-\d{2}$/.test(value)) {
    return endOfMonth(new Date(`${value}-01`));
  }

  return null;
}

export function useReportsSectionController({
  profile,
  columns,
  rowsPerPage,
  sortConfig,
  notify,
  setSelectedRows,
}) {
  const reportsTabData = useReportsTabData({ profile, rowsPerPage, sortConfig });
  const [editingRow, setEditingRow] = useState(null);
  const [dashboardDateSelection, setDashboardDateSelection] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [savingDashboardDate, setSavingDashboardDate] = useState(false);
  const monthOptions = useMemo(() => getDashboardDateMonthOptions(), []);
  const allowedDashboardDateValues = useMemo(
    () => new Set(monthOptions.map((option) => option.value)),
    [monthOptions]
  );
  const selectedDashboardDateLabel = useMemo(() => {
    const allOptions = [
      ...DASHBOARD_DATE_PREDEFINED_OPTIONS,
      ...monthOptions,
    ];

    return allOptions.find((option) => option.value === dashboardDateSelection)?.label || "";
  }, [dashboardDateSelection, monthOptions]);

  const managerOptions = useMemo(() => {
    const values = new Set();
    for (const row of reportsTabData.data || []) {
      if (row?.manager) {
        values.add(String(row.manager).trim());
      }
    }
    return Array.from(values).sort();
  }, [reportsTabData.data]);

  const bookkeeperOptions = useMemo(() => {
    const values = new Set();
    for (const row of reportsTabData.data || []) {
      if (row?.bookkeeper) {
        values.add(String(row.bookkeeper).trim());
      }
    }
    return Array.from(values).sort();
  }, [reportsTabData.data]);

  const managerBookkeeperFilterPopover = useCallback(
    (fieldName, options, context) => {
      const asRecords = options.map((name, index) => ({ id: `${fieldName}-${index}`, name }));
      return (
        <BookkeeperMultiSelectPopover
          value={context.filters[fieldName] || ""}
          onValueChange={(newValue) =>
            context.setFilters((prev) => ({ ...prev, [fieldName]: newValue }))
          }
          bookkeepers={asRecords}
          loadingBookkeepers={false}
          onClose={context.handleFilterMenuClose}
          onSearch={() => {
            context.handleFilterChange(fieldName, context.filters[fieldName], true);
            context.handleFilterMenuClose();
          }}
          onClear={() => {
            context.setFilters((prev) => ({ ...prev, [fieldName]: "" }));
            context.handleFilterChange(fieldName, "", true);
            context.handleFilterMenuClose();
          }}
        />
      );
    },
    []
  );

  const filterPopoverByField = useMemo(
    () => ({
      primaryContact: (context) => (
        <MultiSelectSortFilterPopover
          field="primaryContact"
          headerName="Primary Contact"
          rows={reportsTabData.data}
          filterValue={context.filters.primaryContact || ""}
          sortField={context.sortConfig.field}
          sortDirection={context.sortConfig.direction}
          onFilterValueChange={(nextValue) =>
            context.setFilters((prev) => ({ ...prev, primaryContact: nextValue }))
          }
          onApplyFilter={(nextValue) =>
            context.handleFilterChange("primaryContact", nextValue, true)
          }
          onSortChange={context.handleSort}
          onClose={context.handleFilterMenuClose}
        />
      ),
      accountType: (context) => (
        <MultiSelectSortFilterPopover
          field="accountType"
          headerName="Account Type"
          rows={reportsTabData.data}
          filterValue={context.filters.accountType || ""}
          showSort={false}
          applyButtonLabel="Filter"
          sortField={context.sortConfig.field}
          sortDirection={context.sortConfig.direction}
          onFilterValueChange={(nextValue) =>
            context.setFilters((prev) => ({ ...prev, accountType: nextValue }))
          }
          onApplyFilter={(nextValue) =>
            context.handleFilterChange("accountType", nextValue, true)
          }
          onSortChange={context.handleSort}
          onClose={context.handleFilterMenuClose}
        />
      ),
      manager: (context) => managerBookkeeperFilterPopover("manager", managerOptions, context),
      bookkeeper: (context) =>
        managerBookkeeperFilterPopover("bookkeeper", bookkeeperOptions, context),
    }),
    [
      reportsTabData.data,
      managerBookkeeperFilterPopover,
      managerOptions,
      bookkeeperOptions,
    ]
  );

  const { renderCellContent } = useMemo(
    () =>
      createManagementCellRenderer({
        onNoteClick: undefined,
        isQuickbooksOverview: false,
        dateFormat: "MMMM yyyy",
      }),
    []
  );

  const openDashboardDateEditor = useCallback((row) => {
    const rawDashboardDate = row?.originalCustomer?.dashboard_date || row?.dashboardDate;
    const parsedMonth = rawDashboardDate ? format(parseISO(rawDashboardDate), "yyyy-MM") : "";
    const initialSelection = allowedDashboardDateValues.has(parsedMonth)
      ? parsedMonth
      : "last_month";

    setEditingRow(row);
    setDashboardDateSelection(initialSelection);
    setConfirmDialogOpen(false);
  }, [allowedDashboardDateValues]);

  const closeDashboardDateEditor = useCallback(() => {
    if (savingDashboardDate) {
      return;
    }

    setEditingRow(null);
    setDashboardDateSelection("");
    setConfirmDialogOpen(false);
  }, [savingDashboardDate]);

  const openConfirmDialog = useCallback(() => {
    if (!dashboardDateSelection || savingDashboardDate) {
      return;
    }

    setConfirmDialogOpen(true);
  }, [dashboardDateSelection, savingDashboardDate]);

  const closeConfirmDialog = useCallback(() => {
    if (savingDashboardDate) {
      return;
    }

    setConfirmDialogOpen(false);
  }, [savingDashboardDate]);

  const handleDashboardDateSave = useCallback(async () => {
    if (!editingRow?.id || !dashboardDateSelection || savingDashboardDate) {
      return;
    }

    const endDate = getDashboardDateEndDate(dashboardDateSelection);
    if (!endDate) {
      return;
    }

    const normalizedDashboardDate = format(endDate, "yyyy-MM-dd");

    setSavingDashboardDate(true);
    try {
      const formData = new FormData();
      formData.append("dashboard_date", normalizedDashboardDate);

      await updateCustomerIndvidual(editingRow.id, formData);
      await generateTargets(editingRow.id, normalizedDashboardDate);

      reportsTabData.updateDashboardDate(editingRow.id, normalizedDashboardDate);
      notify?.({
        open: true,
        message: "Dashboard date updated successfully.",
        severity: "success",
      });
      setEditingRow(null);
      setDashboardDateSelection("");
      setConfirmDialogOpen(false);
    } catch {
      notify?.({
        open: true,
        message: "Failed to update dashboard date.",
        severity: "error",
      });
    } finally {
      setSavingDashboardDate(false);
    }
  }, [dashboardDateSelection, editingRow, notify, reportsTabData, savingDashboardDate]);

  const renderRowActionsMenu = useCallback(
    ({ anchorEl, onClose, row }) => (
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        disablePortal={false}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ zIndex: 99999 }}
        PaperProps={{
          sx: {
            boxShadow:
              "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            minWidth: "180px",
            zIndex: 99999,
          },
        }}
      >
        <MenuList sx={{ py: 1 }}>
          <MenuItem
            onClick={() => {
              openDashboardDateEditor(row);
              onClose?.();
            }}
            sx={{ px: 3, py: 1.5 }}
          >
            <ListItemIcon sx={{ minWidth: "32px" }}>
              <Edit fontSize="small" sx={{ color: "#6b7280" }} />
            </ListItemIcon>
            <ListItemText
              primary="Edit"
              primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}
            />
          </MenuItem>

          <Divider sx={{ my: 1 }} />

          <MenuItem
            onClick={() => {
              const customerId = row?.originalCustomer?.id || row?.id;
              if (customerId) {
                window.open(`/client/${customerId}`, "_blank");
              }
              onClose?.();
            }}
            sx={{ px: 3, py: 1.5 }}
          >
            <ListItemIcon sx={{ minWidth: "32px" }}>
              <OpenInNew fontSize="small" sx={{ color: "#6b7280" }} />
            </ListItemIcon>
            <ListItemText
              primary="Go to Account"
              primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}
            />
          </MenuItem>
        </MenuList>
      </Popover>
    ),
    [openDashboardDateEditor]
  );

  const contract = {
    tableProps: {
      columns,
      rows: reportsTabData.data,
      loading: reportsTabData.loading,
      error: reportsTabData.error,
      emptyMessage: "No reports found",
      searchTerm: reportsTabData.searchTerm,
      page: reportsTabData.page,
      serverSidePagination: true,
      hasNextPage: !!reportsTabData.nextCursor,
      hasPrevPage: !!reportsTabData.prevCursor,
      onNextPage: reportsTabData.handleNextPage,
      onPreviousPage: reportsTabData.handlePreviousPage,
      onFiltersChange: reportsTabData.handleFiltersChange,
      filterPopoverByField,
      renderCellContent,
      renderRowActionsMenu,
    },
    toolbar: {
      searchValue: reportsTabData.searchTerm || "",
      onSearchChange: reportsTabData.handleSearchChange,
    },
    dialogs: {
      render: () => (
        <>
          <Dialog
            open={Boolean(editingRow)}
            onClose={closeDashboardDateEditor}
            fullWidth
            maxWidth="xs"
            PaperProps={{
              sx: { borderRadius: 2 },
            }}
            sx={{ zIndex: 99999 }}
          >
            <DialogTitle>Edit Dashboard Date</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 1 }}>
                <FormControl fullWidth>
                  <InputLabel id="reports-dashboard-date-label">Dashboard Date</InputLabel>
                  <Select
                    labelId="reports-dashboard-date-label"
                    value={dashboardDateSelection}
                    label="Dashboard Date"
                    onChange={(event) => setDashboardDateSelection(event.target.value)}
                    disabled={savingDashboardDate}
                    MenuProps={{ sx: { zIndex: 99999 } }}
                  >
                    <ListSubheader disableSticky>Quick Picks</ListSubheader>
                    {DASHBOARD_DATE_PREDEFINED_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                    <ListSubheader disableSticky>Months</ListSubheader>
                    {monthOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={closeDashboardDateEditor} disabled={savingDashboardDate}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={openConfirmDialog}
                disabled={!dashboardDateSelection || savingDashboardDate}
              >
                Update
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={confirmDialogOpen}
            onClose={closeConfirmDialog}
            fullWidth
            maxWidth="xs"
            PaperProps={{
              sx: { borderRadius: 2 },
            }}
            sx={{ zIndex: 100000 }}
          >
            <DialogTitle>Confirm Dashboard Date Update</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                Update the dashboard date for {editingRow?.accountName || editingRow?.originalCustomer?.name || "this account"} to {selectedDashboardDateLabel || "the selected option"}?
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={closeConfirmDialog} disabled={savingDashboardDate}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleDashboardDateSave}
                disabled={savingDashboardDate}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ),
    },
    commands: {
      refresh: reportsTabData.refresh,
      clearSelection: () => setSelectedRows([]),
    },
    meta: {
      sectionId: "reports",
      capabilities: {
        managerOptions: managerOptions.length > 0,
        bookkeeperOptions: bookkeeperOptions.length > 0,
      },
    },
  };

  assertSectionControllerContract("reports", contract);
  return contract;
}
